import { ReactNode, useCallback } from 'react';
import { Form, Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useNode, useEditor } from '@craftjs/core';
import FormSettings from '@/components/editor/settings/FormSettings';
import useResponsiveValues from '../hooks/useResponsiveValues';
import { CssClass } from '@/components/editor/settings/ClassSettings';
import EmptyState from '@/components/EmptyState';
import useSnackbar from '@/hooks/useSnackbar';
import Mustache from "mustache";
import useNormalizeSpacing from '@/components/editor/settings/SpacingSettings/useNormalizeSpacing';

interface Props extends CssClass {
  children: ReactNode;
  to?: string;
  subject?: string;
  message?: string;
  submissionType?: 'email' | 'queryString';
  customQueryParams?: string;
}

const FormBlock = (props: Partial<Props>) => {
  const spacing = useNormalizeSpacing(props);
  const responsive = useResponsiveValues();
  const { id, connectors: { connect, drag } } = useNode();
  const [openSnackbar] = useSnackbar();
  const navigate = useNavigate();

  const { isEmpty } = useEditor((_, query) => ({
    isEmpty: !query.node(id).childNodes().length,
  }));

  const handleSubmit = useCallback(async (values: any) => {
    const submissionType = props.submissionType || 'email';

    if (submissionType === 'queryString') {
      // Build query string from form values
      const queryParams = new URLSearchParams();

      // Add form field values
      Object.keys(values).forEach(key => {
        if (values[key]) {
          queryParams.append(key, values[key]);
        }
      });

      // Add custom query parameters if provided
      if (props.customQueryParams) {
        const customParams = new URLSearchParams(props.customQueryParams);
        customParams.forEach((value, key) => {
          queryParams.append(key, value);
        });
      }

      // Get current URL and append query strings
      const currentUrl = new URL(window.location.href);
      queryParams.forEach((value, key) => {
        currentUrl.searchParams.set(key, value);
      });

      // Update URL without reloading the page using React Router
      const newPath = currentUrl.pathname + currentUrl.search;
      navigate(newPath);
      return;
    }

    // Email submission logic
    // Extract query string parameters from URL
    const queryParams: Record<string, string> = {};
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.forEach((value, key) => {
      queryParams[key] = value;
    });

    debugger

    // Merge form values with query string parameters
    const templateValues = { ...values, ...queryParams };

    const processedMessage = Mustache.render(props.message || '', templateValues);

    const payload = {
      // form config data
      to: props.to,
      subject: props.subject,
      message: processedMessage,
      // users data
      from: 'no-reply@bitelink.app',
    }

    try {
      await fetch('https://sendemail-sujjen5giq-uc.a.run.app', {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
        },
      })
      openSnackbar('Mensaje Enviado', 'success');
    } catch (error) {
      console.error(error);
      openSnackbar('Error al enviar el mensaje', 'error');
    }
  }, [props.to, props.subject, props.message, props.submissionType, props.customQueryParams, openSnackbar]);

  return (
    <div
      style={{
        boxSizing: 'border-box',
        ...spacing,
      }}
      ref={(ref: any) => connect(drag(ref))}
      className={`container ${responsive.getValue(props?.cssClass)}`}
      key={`${id}-${responsive.viewport}`}
    >
      <Formik
        initialValues={{}}
        onSubmit={handleSubmit}
      >
        {() => (
          <Form>
            {isEmpty ? <EmptyState /> : props?.children}
          </Form>
        )}
      </Formik>
    </div>
  );
};

FormBlock.craft = {
  displayName: 'Formulario',
  props: {
    to: '',
    subject: '',
    message: '',
    submissionType: 'email',
    customQueryParams: '',
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
  },
  related: {
    settings: FormSettings,
  },
};

export default FormBlock;
